import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { ActionButton } from '@zeus/core/layout/MainLayout';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useLoginPools } from '_/hooks/useLoginPools';
import useOrgAccess from '_/hooks/useOrgAccess';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Input } from 'antd';
import Table from 'antd/lib/table';
import React, { ReactElement, useCallback, useMemo, useState, useEffect } from 'react';

import { useColumnDefs } from './LoginPools.hooks';

const LoginPools = (): ReactElement => {
  const history = useNavigate();
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const columnDefs = useColumnDefs();
  const [searchText, setSearchText] = useState('');
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();

  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const {
    data: loginPools = [],
    refetch,
    isRefetching,
    isLoading,
  } = useLoginPools(selectedProject?.id, selectedOrgSlug, selectedProjectSlug);

  // Filter login pools based on search text
  const filteredLoginPools = (loginPools as any[]).filter((pool) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return pool.name?.toLowerCase().includes(searchLower) || pool.id?.toLowerCase().includes(searchLower);
  });

  const handleNewLoginPool = useCallback(() => {
    void history({ to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/loginpools/_new` });
  }, [history, selectedOrgSlug, selectedProjectSlug]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        onClick: handleNewLoginPool,
        icon: <PlusCircleOutlined />,
        label: 'Add Login Pool',
        disabled: !access,
        tooltip: !access ? 'You do not have the right permissions to add a login pool' : undefined,
      },
    ],
    [handleNewLoginPool, access],
  );

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'Login Pools' },
    ]);
    setActionButtons(actionButtons);
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    setOnRefresh,
    setRefreshLoading,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    actionButtons,
    handleRefresh,
    isRefetching,
  ]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search login pools..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
          allowClear
        />
      </div>
      <Table columns={columnDefs} dataSource={filteredLoginPools} rowKey="id" pagination={false} loading={isLoading} />
    </div>
  );
};

export default LoginPools;
